import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { contactInfo } from '../../mocks/contactData';

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const contactEndpoint = `${apiBaseUrl}/api/v1/contact`;

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const company = formData.get('company') as string;
    const message = formData.get('message') as string;

    if (message && message.length > 500) {
      setStatus('error');
      return;
    }

    setStatus('submitting');

    try {
      const response = await fetch(contactEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, company, message }),
      });

      if (!response.ok) {
        setStatus('error');
        return;
      }

      setStatus('success');
      form.reset();
      setCharCount(0);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-44 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 50% 0%, rgba(59,130,246,0.08), transparent 55%)',
            }}
          />
        </div>
        <div className="relative w-full px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-5 leading-tight">
              Contact us
            </h1>
            <p className="text-lg md:text-xl text-zinc-500 leading-relaxed max-w-2xl mx-auto">
              Questions about LoadAPI? Want to get your fleet set up? Reach out and we will get back to you within one business day.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <main className="pb-20 md:pb-28">
        <div className="w-full px-6 md:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
              {/* Form Card */}
              <div className="flex-1">
                <div
                  className="bg-white rounded-[18px] border border-zinc-200 p-6 md:p-8 lg:p-10"
                  style={{
                    boxShadow:
                      '0 20px 45px -24px rgba(15,23,42,0.35)',
                  }}
                >
                  {status === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-12 h-12 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-4">
                        <i className="ri-check-line text-2xl text-emerald-600"></i>
                      </div>
                      <h3 className="text-lg font-bold text-zinc-900 mb-2">Message sent</h3>
                      <p className="text-sm text-zinc-500">
                        Thanks for reaching out. We will get back to you within one business day.
                      </p>
                    </div>
                  ) : (
                    <form
                      id="contact-form"
                      onSubmit={handleSubmit}
                      className="space-y-5"
                    >
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-zinc-700 mb-1.5"
                        >
                          Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-md text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all"
                          placeholder="Your full name"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-zinc-700 mb-1.5"
                        >
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-md text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all"
                          placeholder="you@company.com"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="company"
                          className="block text-sm font-medium text-zinc-700 mb-1.5"
                        >
                          Company <span className="text-zinc-400 font-normal">(optional)</span>
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-md text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all"
                          placeholder="Your fleet or company name"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label
                            htmlFor="message"
                            className="block text-sm font-medium text-zinc-700"
                          >
                            Message
                          </label>
                          <span
                            className={`text-xs ${charCount > 500 ? 'text-red-500' : 'text-zinc-400'}`}
                          >
                            {charCount}/500
                          </span>
                        </div>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={5}
                          maxLength={500}
                          onChange={(e) => setCharCount(e.target.value.length)}
                          className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-md text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all resize-none"
                          placeholder="How can we help?"
                        />
                        {status === 'error' && (
                          <p className="text-xs text-red-500 mt-1.5">
                            Something went wrong or the message exceeds 500 characters. Please try again.
                          </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className="w-full sm:w-auto px-7 py-3 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {status === 'submitting' ? 'Sending...' : 'Send message'}
                      </button>
                    </form>
                  )}
                </div>
              </div>

              {/* Info Sidebar */}
              <div className="lg:w-80 flex-shrink-0">
                <div className="space-y-8">
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide mb-4">
                      Contact
                    </h3>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-blue-600/10 rounded-lg flex-shrink-0">
                        <i className="ri-mail-line text-blue-600"></i>
                      </div>
                      <div>
                        <p className="text-sm text-zinc-500 mb-0.5">Email</p>
                        <a
                          href={`mailto:${contactInfo.email}`}
                          className="text-sm font-medium text-zinc-900 hover:text-blue-600 transition-colors"
                        >
                          {contactInfo.email}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-zinc-200 pt-8">
                    <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide mb-4">
                      What to expect
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-time-line text-zinc-400"></i>
                        </div>
                        <p className="text-sm text-zinc-500">
                          Response within one business day
                        </p>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-user-voice-line text-zinc-400"></i>
                        </div>
                        <p className="text-sm text-zinc-500">
                          Direct answers from the product team
                        </p>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-shield-check-line text-zinc-400"></i>
                        </div>
                        <p className="text-sm text-zinc-500">
                          No spam or third-party sharing
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
